/**
 * Serviço de Geocodificação
 * Prioriza a edge function `geocode-cep` (server-side, sem CORS/rate-limit do navegador)
 * e mantém ViaCEP + Nominatim como fallback.
 */

import { supabase } from '@/integrations/supabase/client';

/** Precisão do resultado: `approximate` não deve bloquear área de entrega. */
export type GeocodingPrecision = 'exact' | 'approximate';

export interface GeocodingResult {
  lat: number;
  lng: number;
  address?: string;
  precision?: GeocodingPrecision;
  source?: string;
}


interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
}

// Cache de geocodificação (localStorage)
const CACHE_KEY = 'geocoding_cache';
const CACHE_VERSION = 6; // Incrementar para invalidar cache antigo com geocodificação incorreta
const CACHE_VERSION_KEY = 'geocoding_cache_version';
const CACHE_TTL_DAYS = 30;

// Invalidar cache se versão mudou (corrige coordenadas erradas de bairros ambíguos)
try {
  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
  if (storedVersion !== String(CACHE_VERSION)) {
    localStorage.removeItem(CACHE_KEY);
    localStorage.setItem(CACHE_VERSION_KEY, String(CACHE_VERSION));
  }
} catch {}


interface CacheEntry {
  result: GeocodingResult;
  timestamp: number;
}

interface GeocodingCache {
  [cep: string]: CacheEntry;
}

// Rate limiting para Nominatim (1 req/s)
let lastNominatimRequest = 0;
const NOMINATIM_RATE_LIMIT_MS = 1100; // 1.1 segundos para margem de segurança

/**
 * Carrega o cache do localStorage
 */
function loadCache(): GeocodingCache {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn('[Geocoding] Erro ao carregar cache:', error);
  }
  return {};
}

/**
 * Salva no cache do localStorage
 */
function saveToCache(cep: string, result: GeocodingResult): void {
  try {
    const cache = loadCache();
    cache[cep] = {
      result,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('[Geocoding] Erro ao salvar cache:', error);
  }
}

/**
 * Busca no cache (respeitando TTL)
 */
function getFromCache(cep: string): GeocodingResult | null {
  const cache = loadCache();
  const entry = cache[cep];
  
  if (!entry) return null;
  
  const ttlMs = CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;
  if (Date.now() - entry.timestamp > ttlMs) {
    return null;
  }
  
  return entry.result;
}

/**
 * Aguarda o rate limit do Nominatim
 */
async function waitForRateLimit(): Promise<void> {
  const now = Date.now();
  const timeSinceLastRequest = now - lastNominatimRequest;
  
  if (timeSinceLastRequest < NOMINATIM_RATE_LIMIT_MS) {
    const waitTime = NOMINATIM_RATE_LIMIT_MS - timeSinceLastRequest;
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  
  lastNominatimRequest = Date.now();
}

/**
 * Busca endereço no ViaCEP
 */
async function fetchViaCep(cep: string): Promise<ViaCepResponse | null> {
  const normalizedCep = cep.replace(/\D/g, '');
  
  if (normalizedCep.length !== 8) {
    console.warn('[Geocoding] CEP inválido:', cep);
    return null;
  }
  
  try {
    const response = await fetch(`https://viacep.com.br/ws/${normalizedCep}/json/`);
    const data: ViaCepResponse = await response.json();
    
    if (data.erro) {
      console.warn('[Geocoding] CEP não encontrado:', cep);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('[Geocoding] Erro ao buscar ViaCEP:', error);
    return null;
  }
}

/**
 * Busca coordenadas no Nominatim (OpenStreetMap) usando query livre
 */
async function fetchNominatim(address: string): Promise<GeocodingResult | null> {
  await waitForRateLimit();
  
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1&countrycodes=br`,
      {
        headers: {
          'User-Agent': 'AmeUpEventos/1.0',
        },
      }
    );
    
    const data: NominatimResult[] = await response.json();
    
    if (!data || data.length === 0) {
      return null;
    }
    
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      address: data[0].display_name,
    };
  } catch (error) {
    console.error('[Geocoding] Erro ao buscar Nominatim:', error);
    return null;
  }
}

/**
 * Busca coordenadas no Nominatim usando query estruturada (evita ambiguidade de bairros)
 */
async function fetchNominatimStructured(suburb: string, city: string, state: string): Promise<GeocodingResult | null> {
  await waitForRateLimit();
  
  try {
    const params = new URLSearchParams({
      format: 'json',
      suburb,
      city,
      state,
      country: 'Brazil',
      limit: '1',
    });
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      { headers: { 'User-Agent': 'AmeUpEventos/1.0' } }
    );
    
    const data: NominatimResult[] = await response.json();
    if (!data || data.length === 0) return null;
    
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      address: data[0].display_name,
    };
  } catch (error) {
    console.error('[Geocoding] Erro ao buscar Nominatim estruturado:', error);
    return null;
  }
}

/**
 * Busca coordenadas no Nominatim usando endereço estruturado com rua/CEP
 * para evitar cair no centro da cidade quando o CEP é de bairro distante.
 */
async function fetchNominatimByStreet(
  street: string,
  city: string,
  state: string,
  postalcode?: string,
  suburb?: string,
): Promise<GeocodingResult | null> {
  await waitForRateLimit();

  try {
    const params = new URLSearchParams({
      format: 'json',
      street,
      city,
      state,
      country: 'Brazil',
      limit: '1',
    });

    if (postalcode) params.set('postalcode', postalcode);
    if (suburb) params.set('suburb', suburb);

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`,
      { headers: { 'User-Agent': 'AmeUpEventos/1.0' } }
    );

    const data: NominatimResult[] = await response.json();
    if (!data || data.length === 0) return null;

    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      address: data[0].display_name,
    };
  } catch (error) {
    console.error('[Geocoding] Erro ao buscar Nominatim por rua:', error);
    return null;
  }
}

/**
 * Coordenadas aproximadas baseadas no prefixo do CEP (São Paulo e região)
 * Fallback quando Nominatim não retorna resultados
 */
function getApproximateCoordinates(cep: string): GeocodingResult | null {
  const normalizedCep = cep.replace(/\D/g, '');
  const prefix = normalizedCep.substring(0, 3);
  
  // Mapeamento aproximado de prefixos de CEP para coordenadas (São Paulo e região)
  const prefixCoordinates: { [key: string]: [number, number] } = {
    // Centro de São Paulo
    '010': [-23.5505, -46.6333],
    '011': [-23.5505, -46.6333],
    '012': [-23.5505, -46.6333],
    '013': [-23.5505, -46.6333],
    '014': [-23.5505, -46.6333],
    '015': [-23.5505, -46.6333],
    '016': [-23.5505, -46.6333],
    '017': [-23.5505, -46.6333],
    // Zona Sul
    '040': [-23.6000, -46.6500],
    '041': [-23.6000, -46.6500],
    '042': [-23.6200, -46.6600],
    '043': [-23.6200, -46.6600],
    '044': [-23.6400, -46.6700],
    '045': [-23.6400, -46.6700],
    '046': [-23.6600, -46.6800],
    '047': [-23.6600, -46.6800],
    // Zona Leste
    '030': [-23.5300, -46.5500],
    '031': [-23.5300, -46.5500],
    '032': [-23.5400, -46.5200],
    '033': [-23.5400, -46.5200],
    '034': [-23.5500, -46.4800],
    '035': [-23.5500, -46.4800],
    '036': [-23.5600, -46.4500],
    '037': [-23.5600, -46.4500],
    '038': [-23.5100, -46.4200],
    // Zona Norte
    '020': [-23.4800, -46.6200],
    '021': [-23.4800, -46.6200],
    '022': [-23.4600, -46.6400],
    '023': [-23.4600, -46.6400],
    '024': [-23.4400, -46.6600],
    '025': [-23.4400, -46.6600],
    // Zona Oeste
    '050': [-23.5400, -46.7200],
    '051': [-23.5400, -46.7200],
    '052': [-23.5200, -46.7400],
    '053': [-23.5200, -46.7400],
    '054': [-23.5000, -46.7600],
    '055': [-23.5000, -46.7600],
    // ABC Paulista
    '090': [-23.6600, -46.5300],
    '091': [-23.6600, -46.5300],
    '092': [-23.7000, -46.5500],
    '093': [-23.7000, -46.5500],
    '094': [-23.6200, -46.5800],
    '095': [-23.6200, -46.5800],
    // Guarulhos
    '070': [-23.4500, -46.5300],
    '071': [-23.4500, -46.5300],
    '072': [-23.4300, -46.5500],
    // Itaquera / Zona Leste extrema
    '080': [-23.5150, -46.4550],
    '081': [-23.5200, -46.4400],
    '082': [-23.5160, -46.4480],
    '083': [-23.5300, -46.4300],
    '084': [-23.5400, -46.4100],
    // Osasco
    '060': [-23.5300, -46.7900],
    '061': [-23.5300, -46.7900],
    '062': [-23.5100, -46.8100],
    '063': [-23.5100, -46.8100],
    // Santos e Baixada Santista
    '110': [-23.9600, -46.3300],
    '111': [-23.9600, -46.3300],
    '112': [-23.9500, -46.3000],
    '113': [-23.9500, -46.3000],
    // Campinas
    '130': [-22.9100, -47.0600],
    '131': [-22.9100, -47.0600],
    '132': [-22.9000, -47.0500],
    // Sorocaba
    '180': [-23.5000, -47.4500],
    '181': [-23.5000, -47.4500],
  };
  
  const coords = prefixCoordinates[prefix];
  if (coords) {
    return {
      lat: coords[0],
      lng: coords[1],
      address: `Região aproximada (CEP ${normalizedCep})`,
      precision: 'approximate',
      source: 'cep-prefix',

    };
  }
  
  // Fallback para centro de São Paulo se não encontrar prefixo
  if (normalizedCep.startsWith('0') || normalizedCep.startsWith('1')) {
    return {
      lat: -23.5505,
      lng: -46.6333,
      address: `Centro de São Paulo (aproximado)`,
      precision: 'approximate',
      source: 'cep-prefix',

    };
  }
  
  return null;
}

/**
 * Geocodificação via edge function (BrasilAPI → AwesomeAPI → Nominatim server-side).
 * Retorna null em qualquer falha para permitir o fallback client-side.
 */
async function geocodeCepServerSide(cep: string): Promise<GeocodingResult | null> {
  try {
    const { data, error } = await supabase.functions.invoke('geocode-cep', { body: { cep } });
    if (error) {
      console.warn('[Geocoding] Falha na geocodificação server-side:', error.message);
      return null;
    }

    const lat = Number((data as any)?.lat);
    const lng = Number((data as any)?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    return {
      lat,
      lng,
      address: (data as any)?.address,
      precision: ((data as any)?.precision as GeocodingPrecision) ?? 'exact',
      source: (data as any)?.source ?? 'edge',
    };
  } catch (error) {
    console.warn('[Geocoding] Erro inesperado na geocodificação server-side:', error);
    return null;
  }
}

/**
 * Geocodifica um CEP em coordenadas geográficas
 * 
 * @param cep - CEP a ser geocodificado
 * @returns Coordenadas {lat, lng} ou null se não encontrado
 */

export async function geocodeCep(cep: string): Promise<GeocodingResult | null> {
  const normalizedCep = cep.replace(/\D/g, '');
  
  if (normalizedCep.length !== 8) {
    console.warn('[Geocoding] CEP inválido:', cep);
    return null;
  }
  
  // 1. Verificar cache
  const cached = getFromCache(normalizedCep);
  if (cached) {
    return cached;
  }

  // 1.1 Geocodificação server-side (fonte preferencial: sem CORS nem rate limit do navegador)
  const serverResult = await geocodeCepServerSide(normalizedCep);
  if (serverResult) {
    saveToCache(normalizedCep, serverResult);
    return serverResult;
  }


  
  // 2. Buscar endereço no ViaCEP
  const viaCepData = await fetchViaCep(normalizedCep);
  if (!viaCepData) {
    // Tentar coordenadas aproximadas
    const approximate = getApproximateCoordinates(normalizedCep);
    if (approximate) {
      saveToCache(normalizedCep, approximate);
    }
    return approximate;
  }
  
  // 3. Tentar busca mais precisa por rua/CEP antes da query livre.
  // Isso evita falso positivo de frete grátis quando o Nominatim responde
  // apenas o centro da cidade para CEPs periféricos.
  let result: GeocodingResult | null = null;

  if (viaCepData.logradouro) {
    result = await fetchNominatimByStreet(
      viaCepData.logradouro,
      viaCepData.localidade,
      viaCepData.uf,
      normalizedCep,
      viaCepData.bairro,
    );
  }

  // 4. Montar query de endereço para Nominatim
  const addressParts = [
    viaCepData.logradouro,
    viaCepData.bairro,
    viaCepData.localidade,
    viaCepData.uf,
    'Brasil',
  ].filter(Boolean);
  
  const fullAddress = addressParts.join(', ');
  
  // 5. Buscar coordenadas no Nominatim com query livre
  if (!result) {
    result = await fetchNominatim(fullAddress);
  }
  
  // Ignorar resultados genéricos de município/estado. Eles distorcem a distância.
  const isTooGenericMunicipalityResult =
    result?.address?.trim() === `${viaCepData.localidade}, Região Sudeste, Brasil` ||
    result?.address?.trim() === `${viaCepData.localidade}, ${viaCepData.uf}, Brasil`;

  if (isTooGenericMunicipalityResult) {
    result = null;
  }

  // 6. Fallback: tentar cidade + estado (mais confiável que bairro solto)
  if (!result) {
    const cityAddress = `${viaCepData.localidade}, ${viaCepData.uf}, Brasil`;
    const cityResult = await fetchNominatim(cityAddress);
    // Nível de cidade não localiza a célula H3 do cliente: marcar como aproximado
    result = cityResult ? { ...cityResult, precision: 'approximate' } : null;
  }

  
  // Se ainda veio um resultado genérico de cidade, desconsidera antes do próximo fallback.
  if (
    result?.address?.trim() === `${viaCepData.localidade}, Região Sudeste, Brasil` ||
    result?.address?.trim() === `${viaCepData.localidade}, ${viaCepData.uf}, Brasil`
  ) {
    result = null;
  }

  // 7. Fallback: tentar busca estruturada com bairro dentro da cidade
  if (!result && viaCepData.bairro) {
    const structuredQuery = `${viaCepData.bairro}, ${viaCepData.localidade}, ${viaCepData.uf}`;
    result = await fetchNominatimStructured(viaCepData.bairro, viaCepData.localidade, viaCepData.uf);
    if (!result) {
      result = await fetchNominatim(structuredQuery);
    }
  }

  if (
    result?.address?.trim() === `${viaCepData.localidade}, Região Sudeste, Brasil` ||
    result?.address?.trim() === `${viaCepData.localidade}, ${viaCepData.uf}, Brasil`
  ) {
    result = null;
  }
  
  // 8. Último fallback: coordenadas aproximadas
  if (!result) {
    result = getApproximateCoordinates(normalizedCep);
  }
  
  // 9. Salvar no cache e retornar
  if (result) {
    saveToCache(normalizedCep, result);
  }
  
  return result;
}

/**
 * Limpa o cache de geocodificação
 */
export function clearGeocodingCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('[Geocoding] Erro ao limpar cache:', error);
  }
}
